/**
 * Dashboard hydrator.
 *
 * Loads GET /organisation/dashboard-summary ONCE on dashboard mount and
 * hydrates the visit + sitting stores from it, so the ~12 dashboard widgets
 * stop each firing their own fetch on mount (the cause of the refresh 429s).
 *
 * How widgets stop fetching: we `preregister` each data slice against the
 * single summary request under the SAME coalescing key the store fetch
 * actions use (utils/coalesce.js + stores/dataKeys.js). When a widget mounts
 * microseconds later and calls e.g. `fetchMyQueue()`, the action finds the
 * key already in flight and piggybacks on it — zero extra network calls.
 *
 * Safety / zero-breakage:
 *  - Section present  → set store state from it.
 *  - Section null     → leave as-is. A section is null only when the caller
 *    lacks permission (the standalone endpoint would 403 too) or it errored
 *    server-side (next poll refills) — so we must NOT re-fetch it, or we'd
 *    fire the very calls we're trying to avoid (and 403s).
 *  - Whole summary REJECTS → fall back to the individual fetch for every
 *    slice (via { force: true }, bypassing coalescing), i.e. exactly the
 *    pre-change per-widget behaviour.
 *
 * This never touches the realtime path (WebSocket → eventBus → store
 * patches), so same-user and cross-role live updates are unaffected.
 */
import { fetchDashboardSummary } from './api/dashboardApi';
import { mapDoctorsResponse } from './api/visitApi';
import { preregister, isInflight } from '../utils/coalesce';
import { DATA_KEYS } from '../stores/dataKeys';
import useVisitStore from '../stores/visitStore';
import useSittingStore from '../stores/sittingStore';

export function hydrateDashboard(date) {
  // Re-entry guard: if a hydration (or any daily fetch) is already in
  // flight, don't start a second summary request. Cheaply absorbs React
  // StrictMode's double-invoke without a time-based cooldown.
  if (isInflight(DATA_KEYS.daily)) return undefined;

  const summaryP = fetchDashboardSummary(date);

  const visit = () => useVisitStore.getState();
  const sitting = () => useSittingStore.getState();

  // ── visit store slices ──────────────────────────────────────────
  preregister(DATA_KEYS.daily, summaryP.then(
    (s) => {
      if (s && s.visits) {
        useVisitStore.setState({ dailyVisits: s.visits.visits || [], error: null });
      }
    },
    () => visit().fetchDailyVisits(undefined, { force: true }),
  ));

  preregister(DATA_KEYS.doctors, summaryP.then(
    (s) => {
      if (s && s.doctors) {
        useVisitStore.setState({ doctors: mapDoctorsResponse(s.doctors), error: null });
      }
    },
    () => visit().fetchDoctors({ force: true }),
  ));

  preregister(DATA_KEYS.stats, summaryP.then(
    (s) => {
      if (s && s.consultation_stats) {
        useVisitStore.setState({ consultationStats: s.consultation_stats });
      }
    },
    () => visit().loadConsultationStats(date, { force: true }),
  ));

  // ── sitting store slices ────────────────────────────────────────
  preregister(DATA_KEYS.myQueue, summaryP.then(
    (s) => {
      const sec = s && s.sittings && s.sittings.assigned;
      if (sec) useSittingStore.setState({ myQueue: sec.sittings || [] });
    },
    () => sitting().fetchMyQueue({}, { force: true }),
  ));

  preregister(DATA_KEYS.delegated, summaryP.then(
    (s) => {
      const sec = s && s.sittings && s.sittings.delegated_by_me;
      if (sec) useSittingStore.setState({ delegatedByMe: sec.sittings || [] });
    },
    () => sitting().fetchDelegatedByMe({ force: true }),
  ));

  preregister(DATA_KEYS.actionPending, summaryP.then(
    (s) => {
      const sec = s && s.sittings && s.sittings.action_pending;
      if (sec) useSittingStore.setState({ actionPendingSittings: sec.sittings || [] });
    },
    () => sitting().fetchActionPendingSittings({}, { force: true }),
  ));

  return summaryP;
}

export default hydrateDashboard;
